import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  ShoppingCartIcon,
  UserIcon,
  Bars3Icon as MenuIcon,
  XMarkIcon as XIcon,
  HeartIcon,
  ArrowRightOnRectangleIcon as LogoutIcon,
  CogIcon,
  HomeIcon,
  BeakerIcon,
  CubeIcon,
  LightBulbIcon,
  WalletIcon,
  LanguageIcon,
  SparklesIcon,
  AcademicCapIcon,
} from '@heroicons/react/24/outline';
import Logo from '../common/Logo';
import { selectAuth, selectIsAuthenticated, selectIsAdmin, logout } from '../../store/slices/authSlice';
// Removed cart UI usage for buy-now flow
import { setMobileMenuOpen } from '../../store/slices/uiSlice';

const Navbar = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, lab } = useSelector(selectAuth);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const isAdmin = useSelector(selectIsAdmin);
  // const cartItemsCount = useSelector(selectCartItemsCount);
  
  const [scrolled, setScrolled] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = async () => {
    await dispatch(logout());
    navigate('/');
  };

  const handleLanguageChange = () => {
    const newLang = i18n.language === 'ar' ? 'en' : 'ar';
    i18n.changeLanguage(newLang);
  };

  // Check if lab has active premium subscription
  const isLabPremium = lab?.isPremium && lab?.premiumUntil && new Date(lab.premiumUntil) > new Date();

  const navLinks = [
    { name: t('nav.home'), path: '/', icon: HomeIcon },
    { name: t('nav.products'), path: '/products', icon: CubeIcon },
    { name: 'المراكز البحثية', path: '/research-centers', icon: AcademicCapIcon },
    { name: t('nav.consultingServices'), path: '/consulting-services', icon: LightBulbIcon },
    { name: t('nav.customLabServices'), path: '/custom-lab-services', icon: BeakerIcon },
    { name: t('nav.techSolutions'), path: '/tech-solutions', icon: CogIcon },
    // Only show subscription link for non-premium labs
    ...(user?.role === 'lab' && !isLabPremium ? [{ name: t('subscription.badeelPlus'), path: '/subscription-plans', icon: SparklesIcon, isSpecial: true }] : []),
  ];

  const userMenuItems = [
    { name: t('nav.dashboard'), path: isAdmin ? '/admin' : '/lab', icon: CogIcon },
    { name: t('nav.myOrders'), path: '/my-orders', icon: ShoppingCartIcon },
  ];

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled 
            ? 'bg-white/95 backdrop-blur-lg shadow-xl border-b border-gray-100' 
            : 'bg-white/80 backdrop-blur-md'
        }`}
      >
        <div className="container mx-auto px-4 lg:px-8">
         <div className="flex items-center justify-between h-16 sm:h-18 lg:h-20 gap-4 lg:gap-8">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 lg:gap-3 flex-shrink-0">
              <Logo size="md" animated={true} />
              <span className="text-gray-700 text-xs hidden 2xl:block max-w-xs truncate">{t('home.hero.title')}</span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-3 xl:gap-5 flex-1 justify-center">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`group relative whitespace-nowrap ${
                    link.isSpecial
                      ? 'px-3 lg:px-4 py-1.5 lg:py-2 rounded-lg bg-gradient-to-r from-brand-blue to-brand-green shadow-lg hover:from-brand-blueSecondary hover:to-brand-blue'
                      : ''
                  }`}
                >
                  <span className={`font-medium transition-colors flex items-center gap-1.5 text-sm ${
                    link.isSpecial
                      ? 'text-white'
                      : 'text-gray-700 hover:text-brand-blue'
                  }`}>
                    {link.isSpecial && <link.icon className="w-4 h-4 animate-pulse" />}
                    <span>{link.name}</span>
                    {link.isSpecial && <SparklesIcon className="w-3 h-3 lg:w-4 lg:h-4" />}
                  </span>
                  {!link.isSpecial && (
                    <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-brand-blue to-brand-blueSecondary transition-all group-hover:w-full"></span>
                  )}
                </Link>
              ))}
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2 lg:gap-3 flex-shrink-0">
              {/* Language Switcher */}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleLanguageChange}
                className="p-2 lg:p-2 rounded-lg hover:bg-gray-100 transition-colors flex items-center gap-1.5"
              >
                <LanguageIcon className="w-5 h-5 lg:w-6 lg:h-6 text-gray-700" />
                <span className="text-xs lg:text-sm font-medium text-gray-700 hidden md:block">
                  {t('nav.changeLanguage')}
                </span>
              </motion.button>

              {/* Cart removed */}

              {/* User Menu */}
              {isAuthenticated ? (
                <div className="relative">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                    className="flex items-center gap-1.5 lg:gap-2 p-2 lg:p-2 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <div className="w-7 h-7 lg:w-8 lg:h-8 bg-gradient-to-r from-brand-blue to-brand-blueSecondary rounded-full flex items-center justify-center text-white font-bold text-sm lg:text-base">
                      {user?.name?.charAt(0)}
                    </div>
                    <span className="text-gray-700 font-medium hidden lg:block text-sm lg:text-base max-w-24 xl:max-w-none truncate">{user?.name}</span>
                  </motion.button>

                  <AnimatePresence>
                    {userMenuOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        className="absolute left-0 mt-2 w-56 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden"
                      >
                        <div className="p-4 border-b border-gray-100">
                          <p className="text-sm font-medium text-gray-900">{user?.name}</p>
                          <p className="text-xs text-gray-500">{user?.email}</p>
                        </div>
                        <div className="py-2">
                          {userMenuItems.map((item) => (
                            <Link
                              key={item.path}
                              to={item.path}
                              onClick={() => setUserMenuOpen(false)}
                              className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                            >
                              <item.icon className="w-4 h-4 ml-3" />
                              {item.name}
                            </Link>
                          ))}
                          <button
                            onClick={handleLogout}
                            className="w-full flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                          >
                            <LogoutIcon className="w-4 h-4 ml-3" />
                            {t('nav.logout')}
                          </button>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                // على الشاشات الصغيرة نخفي الأزرار من الهيدر ونظهرها في قائمة الموبايل فقط
                <div className="hidden lg:flex items-center gap-2">
                  <Link
                    to="/login"
                    className="px-3 lg:px-4 py-1.5 lg:py-2 text-gray-700 hover:text-brand-blue font-medium transition-colors text-sm lg:text-base"
                  >
                    {t('nav.login')}
                  </Link>
                  <Link
                    to="/register"
                    className="px-3 lg:px-4 py-1.5 lg:py-2 bg-gradient-to-r from-brand-blue to-brand-blueSecondary text-white rounded-lg hover:shadow-lg transition-all text-sm lg:text-base whitespace-nowrap"
                  >
                    {t('nav.register')}
                  </Link>
                </div>
              )}

              {/* Mobile Menu Toggle */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                {mobileMenuOpen ? (
                  <XIcon className="w-5 h-5 text-gray-700" />
                ) : (
                  <MenuIcon className="w-5 h-5 text-gray-700" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="lg:hidden bg-white border-t border-gray-100"
            >
              <div className="container mx-auto px-4 py-4">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                  >
                    <link.icon className="w-5 h-5" />
                    <span className="font-medium">{link.name}</span>
                  </Link>
                ))}
                {!isAuthenticated && (
                  <div className="mt-2 border-t border-gray-100 pt-2">
                    <Link
                      to="/login"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                    >
                      <UserIcon className="w-5 h-5" />
                      <span className="font-medium">{t('nav.login')}</span>
                    </Link>
                    <Link
                      to="/register"
                      onClick={() => setMobileMenuOpen(false)}
                      className="mt-2 flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-brand-blue to-brand-blueSecondary text-white rounded-lg transition-all"
                    >
                      <SparklesIcon className="w-5 h-5" />
                      <span className="font-medium">{t('nav.register')}</span>
                    </Link>
                  </div>
                )}
                
                {/* Language Switcher in Mobile Menu */}
                <button
                  onClick={() => {
                    handleLanguageChange();
                    setMobileMenuOpen(false);
                  }}
                  className="flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors w-full"
                >
                  <LanguageIcon className="w-5 h-5" />
                  <span className="font-medium">{t('nav.changeLanguage')}</span>
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.nav>

      {/* Cart drawer removed */}
    </>
  );
};

export default Navbar;